/**
 * Custom Image extension for the DOCX editor with:
 *  - Resizable handles (drag corners to resize)
 *  - Alignment attribute (left, center, right)
 *  - Persisted width/height in HTML attributes
 *  - Visual selection outline
 *
 * Replaces the default @tiptap/extension-image in DocxEditor.
 */

import { Node, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

export type ImageAlignment = "left" | "center" | "right";

export interface DocxImageOptions {
  allowBase64: boolean;
  HTMLAttributes: Record<string, unknown>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    docxImage: {
      setImage: (options: {
        src: string;
        alt?: string;
        title?: string;
        width?: number | string;
        height?: number | string;
        alignment?: ImageAlignment;
      }) => ReturnType;
      setImageAlignment: (alignment: ImageAlignment) => ReturnType;
      setImageSize: (width: number, height?: number) => ReturnType;
    };
  }
}

const imagePluginKey = new PluginKey("docxImageResize");

function createResizePlugin(): Plugin {
  let activeHandle: {
    node: HTMLImageElement;
    startX: number;
    startY: number;
    startW: number;
    startH: number;
    pos: number;
  } | null = null;

  return new Plugin({
    key: imagePluginKey,
    props: {
      handleDOMEvents: {
        mousedown(view: EditorView, event: MouseEvent) {
          const target = event.target as HTMLElement;
          if (!target.classList.contains("docx-image-handle")) return false;

          const wrapper = target.closest(".docx-image-wrapper");
          const img = wrapper?.querySelector("img");
          if (!img) return false;

          const pos = view.posAtDOM(wrapper!, 0);
          activeHandle = {
            node: img,
            startX: event.clientX,
            startY: event.clientY,
            startW: img.offsetWidth,
            startH: img.offsetHeight,
            pos,
          };

          event.preventDefault();
          event.stopPropagation();

          const onMouseMove = (e: MouseEvent) => {
            if (!activeHandle) return;
            const dx = e.clientX - activeHandle.startX;
            const newW = Math.max(50, activeHandle.startW + dx);
            const ratio = activeHandle.startH / activeHandle.startW;
            const newH = Math.round(newW * ratio);
            activeHandle.node.style.width = `${newW}px`;
            activeHandle.node.style.height = `${newH}px`;
          };

          const onMouseUp = (e: MouseEvent) => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
            if (!activeHandle) return;

            const dx = e.clientX - activeHandle.startX;
            const newW = Math.max(50, activeHandle.startW + dx);
            const ratio = activeHandle.startH / activeHandle.startW;
            const newH = Math.round(newW * ratio);

            const resolvedPos = view.state.doc.resolve(activeHandle.pos);
            const nodePos = resolvedPos.pos;
            const node = view.state.doc.nodeAt(nodePos);

            if (node && node.type.name === "docxImage") {
              const tr = view.state.tr.setNodeMarkup(nodePos, undefined, {
                ...node.attrs,
                width: newW,
                height: newH,
              });
              view.dispatch(tr);
            }

            activeHandle = null;
          };

          document.addEventListener("mousemove", onMouseMove);
          document.addEventListener("mouseup", onMouseUp);
          return true;
        },
      },
    },
  });
}

export const DocxImage = Node.create<DocxImageOptions>({
  name: "docxImage",
  group: "block",
  draggable: true,
  atom: true,

  addOptions() {
    return {
      allowBase64: true,
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
      width: { default: null },
      height: { default: null },
      alignment: { default: "center" },
    };
  },

  parseHTML() {
    return [
      {
        tag: "img[src]",
        getAttrs: (el) => {
          const dom = el as HTMLElement;
          return {
            src: dom.getAttribute("src"),
            alt: dom.getAttribute("alt"),
            title: dom.getAttribute("title"),
            width: dom.getAttribute("width") ? Number(dom.getAttribute("width")) : null,
            height: dom.getAttribute("height") ? Number(dom.getAttribute("height")) : null,
            alignment: dom.getAttribute("data-alignment") || dom.style?.textAlign || "center",
          };
        },
      },
      {
        tag: "figure",
        getAttrs: (el) => {
          const dom = el as HTMLElement;
          const img = dom.querySelector("img");
          if (!img) return false;
          return {
            src: img.getAttribute("src"),
            alt: img.getAttribute("alt"),
            title: img.getAttribute("title"),
            width: img.getAttribute("width") ? Number(img.getAttribute("width")) : null,
            height: img.getAttribute("height") ? Number(img.getAttribute("height")) : null,
            alignment: dom.getAttribute("data-alignment") || "center",
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const alignment = HTMLAttributes.alignment || "center";
    const imgAttrs: Record<string, unknown> = {
      src: HTMLAttributes.src,
      alt: HTMLAttributes.alt,
      title: HTMLAttributes.title,
    };
    if (HTMLAttributes.width) imgAttrs.width = HTMLAttributes.width;
    if (HTMLAttributes.height) imgAttrs.height = HTMLAttributes.height;
    imgAttrs["data-alignment"] = alignment;

    return [
      "img",
      mergeAttributes(this.options.HTMLAttributes, imgAttrs, {
        class: `docx-image docx-image-${alignment}`,
      }),
    ];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const dom = document.createElement("div");
      dom.className = `docx-image-wrapper docx-image-align-${node.attrs.alignment || "center"}`;
      dom.setAttribute("data-alignment", node.attrs.alignment || "center");
      dom.draggable = true;

      const img = document.createElement("img");
      img.src = node.attrs.src || "";
      img.alt = node.attrs.alt || "";
      if (node.attrs.title) img.title = node.attrs.title;
      if (node.attrs.width) {
        img.style.width = `${node.attrs.width}px`;
        img.style.height = node.attrs.height ? `${node.attrs.height}px` : "auto";
      }
      img.className = "docx-image";
      img.draggable = false;

      const handleSE = document.createElement("div");
      handleSE.className = "docx-image-handle docx-image-handle-se";
      const handleSW = document.createElement("div");
      handleSW.className = "docx-image-handle docx-image-handle-sw";
      const handleE = document.createElement("div");
      handleE.className = "docx-image-handle docx-image-handle-e";

      dom.appendChild(img);
      dom.appendChild(handleSE);
      dom.appendChild(handleSW);
      dom.appendChild(handleE);

      dom.addEventListener("click", () => {
        if (typeof getPos === "function") {
          const pos = getPos();
          if (pos != null) {
            editor.commands.setNodeSelection(pos);
          }
        }
      });

      return {
        dom,
        update(updatedNode) {
          if (updatedNode.type.name !== "docxImage") return false;
          img.src = updatedNode.attrs.src || "";
          img.alt = updatedNode.attrs.alt || "";
          if (updatedNode.attrs.title) img.title = updatedNode.attrs.title;
          if (updatedNode.attrs.width) {
            img.style.width = `${updatedNode.attrs.width}px`;
            img.style.height = updatedNode.attrs.height ? `${updatedNode.attrs.height}px` : "auto";
          } else {
            img.style.width = "";
            img.style.height = "";
          }
          const alignment = updatedNode.attrs.alignment || "center";
          dom.className = `docx-image-wrapper docx-image-align-${alignment}`;
          dom.setAttribute("data-alignment", alignment);
          return true;
        },
        selectNode() {
          dom.classList.add("docx-image-selected");
        },
        deselectNode() {
          dom.classList.remove("docx-image-selected");
        },
        destroy() {},
      };
    };
  },

  addCommands() {
    return {
      setImage:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
      setImageAlignment:
        (alignment) =>
        ({ state, dispatch }) => {
          const { selection } = state;
          const node = state.doc.nodeAt(selection.from);
          if (!node || node.type.name !== this.name) return false;
          if (dispatch) {
            dispatch(
              state.tr.setNodeMarkup(selection.from, undefined, {
                ...node.attrs,
                alignment,
              }),
            );
          }
          return true;
        },
      setImageSize:
        (width, height) =>
        ({ state, dispatch }) => {
          const { selection } = state;
          const node = state.doc.nodeAt(selection.from);
          if (!node || node.type.name !== this.name) return false;
          if (dispatch) {
            dispatch(
              state.tr.setNodeMarkup(selection.from, undefined, {
                ...node.attrs,
                width,
                height: height ?? null,
              }),
            );
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [createResizePlugin()];
  },
});
